import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpService } from 'src/app/services/http-service.service';
import { LanguageService } from 'src/app/services/language.service';
import { BranchesService } from 'src/app/services/branches.service';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { PromotionDetailModalComponent } from 'src/app/shared/promotion-detail-modal/promotion-detail-modal.component';
import { Subscription } from 'rxjs';

const ALL_CURRENCIES_FILTER = 'ALL';
const PROMO_CARD_WIDTH = 300;

@Component({
  selector: 'divisas-capital-promociones',
  templateUrl: './promociones.component.html',
  styleUrls: ['./promociones.component.scss'],
})
export class PromocionesComponent implements OnInit, OnDestroy {
  @ViewChild('promoScroller') promoScroller?: ElementRef<HTMLElement>;

  public isPromociones = false;
  public promociones: any[] = [];
  public currencyFilters: string[] = [];
  public selectedCurrency: string = ALL_CURRENCIES_FILTER;
  public readonly allCurrenciesFilter = ALL_CURRENCIES_FILTER;
  public activePromoIndex = 0;
  public promoDots: number[] = [];

  private branchId = 0;
  private allPromotions: any[] = [];
  private branchPromotions: any[] = [];
  private languageSub?: Subscription;
  private branchSub?: Subscription;
  private branchSelectedSub?: Subscription;

  constructor(
    private readonly _httpService: HttpService,
    private readonly dialog: MatDialog,
    private readonly overlay: Overlay,
    private readonly languageService: LanguageService,
    private readonly branchesService: BranchesService,
  ) {}

  ngOnInit(): void {
    this.branchSub = this.branchesService.sucursalId.subscribe(
      (branchId: number) => {
        if (branchId && branchId !== 0 && this.branchId !== branchId) {
          this.branchId = branchId;
          this.applyBranchFilter();
        }
      },
    );

    this.branchSelectedSub = this.branchesService
      .getBranchSelected()
      .subscribe((branch) => {
        if (branch?.id && this.branchId !== branch.id) {
          this.branchId = branch.id;
          this.applyBranchFilter();
        }
      });

    this.languageSub = this.languageService.currentLanguage$.subscribe(() => {
      this.loadPromotions();
    });
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
    this.branchSub?.unsubscribe();
    this.branchSelectedSub?.unsubscribe();
  }

  selectCurrencyFilter(currency: string): void {
    this.selectedCurrency = currency;
    this.applyCurrencyFilter();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updatePromoDots();
  }

  onPromoScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const lastIndex = this.promoDots.length - 1;
    if (lastIndex <= 0) {
      this.activePromoIndex = 0;
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft >= maxScroll - 1) {
      this.activePromoIndex = lastIndex;
      return;
    }

    const index = Math.round(el.scrollLeft / this.getPromoStep(el));
    this.activePromoIndex = Math.max(0, Math.min(index, lastIndex));
  }

  scrollToPromo(index: number): void {
    const el = this.promoScroller?.nativeElement;
    if (!el) {
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    const left = Math.min(index * this.getPromoStep(el), maxScroll);
    el.scrollTo({ left, behavior: 'smooth' });
    this.activePromoIndex = Math.max(
      0,
      Math.min(index, this.promoDots.length - 1),
    );
  }

  isCurrencySelected(currency: string): boolean {
    return this.selectedCurrency === currency;
  }

  openPromocionDetail(promocion: any): void {
    const isMobile = window.innerWidth < 768;
    const viewportHeight = isMobile ? '90vh' : '85vh';
    const config: MatDialogConfig = {
      data: promocion,
      closeOnNavigation: true,
      panelClass: ['custom-modalbox', 'promotion-detail-modalbox'],
      position: { bottom: '0', right: '0' },
      width: isMobile ? '100vw' : 'min(800px, calc(100vw - 2rem))',
      maxWidth: isMobile ? '100vw' : 'min(800px, calc(100vw - 2rem))',
      height: viewportHeight,
      maxHeight: viewportHeight,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    };

    this.dialog
      .open(PromotionDetailModalComponent, config)
      .afterClosed()
      .subscribe();
  }

  openPromocionReserva(promocion: any): void {
    const isMobile = window.innerWidth < 768;
    const config: MatDialogConfig = {
      data: {
        sucursal: promocion.branch,
        promotion: promocion,
      },
      closeOnNavigation: true,
      disableClose: true,
      panelClass: ['custom-modalbox', 'promotion-detail-modalbox'],
      position: { bottom: '0', right: '0' },
      maxHeight: isMobile ? '85vh' : '70vh',
    };

    this.dialog.open(ModalComponent, config).afterClosed().subscribe();
  }

  private loadPromotions(): void {
    this._httpService.getPromotions().subscribe(({ data }: any) => {
      this.allPromotions = Array.isArray(data) ? data : [];
      this.applyBranchFilter();
    });
  }

  private applyBranchFilter(): void {
    if (!this.branchId) {
      this.branchPromotions = [];
      this.promociones = [];
      this.currencyFilters = [];
      this.selectedCurrency = ALL_CURRENCIES_FILTER;
      this.isPromociones = false;
      this.resetPromoScroller();
      return;
    }

    const now = new Date();
    this.branchPromotions = this.allPromotions
      .filter(
        (x: any) =>
          x?.branch?.id === this.branchId &&
          (!x.end || new Date(x.end) > now),
      )
      .map((x: any) => ({
        ...x,
        short_title:
          x.title.length >= 19 ? x.title.substring(0, 19) + '...' : x.title,
      }));

    this.currencyFilters = this.buildCurrencyFilters(this.branchPromotions);

    if (
      this.selectedCurrency !== ALL_CURRENCIES_FILTER &&
      !this.currencyFilters.includes(this.selectedCurrency)
    ) {
      this.selectedCurrency = ALL_CURRENCIES_FILTER;
    }

    this.isPromociones = this.branchPromotions.length > 0;
    this.applyCurrencyFilter();
  }

  private applyCurrencyFilter(): void {
    if (this.selectedCurrency === ALL_CURRENCIES_FILTER) {
      this.promociones = [...this.branchPromotions];
    } else {
      this.promociones = this.branchPromotions.filter(
        (promotion) =>
          this.getPromotionCurrencyCode(promotion) === this.selectedCurrency,
      );
    }

    this.resetPromoScroller();
  }

  private readScrollerGap(scroller: HTMLElement): number {
    const styles = getComputedStyle(scroller);
    const gap = parseFloat(styles.columnGap || styles.gap);
    return Number.isFinite(gap) ? gap : 16;
  }

  private getPromoStep(scroller: HTMLElement): number {
    const card = scroller.firstElementChild as HTMLElement | null;
    const cardWidth = card?.getBoundingClientRect().width || PROMO_CARD_WIDTH;
    return cardWidth + this.readScrollerGap(scroller);
  }

  private updatePromoDots(): void {
    const el = this.promoScroller?.nativeElement;
    if (!el || this.promociones.length <= 1) {
      this.promoDots = [];
      this.activePromoIndex = 0;
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    const positions =
      maxScroll <= 1 ? 1 : Math.ceil(maxScroll / this.getPromoStep(el)) + 1;
    const count = Math.min(positions, this.promociones.length);

    this.promoDots = count > 1 ? Array.from({ length: count }, (_, i) => i) : [];
    this.activePromoIndex = Math.min(
      this.activePromoIndex,
      Math.max(0, count - 1),
    );
  }

  private resetPromoScroller(): void {
    this.activePromoIndex = 0;
    setTimeout(() => {
      this.promoScroller?.nativeElement.scrollTo({ left: 0 });
      this.updatePromoDots();
    });
  }

  private buildCurrencyFilters(promotions: any[]): string[] {
    const seen = new Set<string>();
    const codes: string[] = [];

    for (const promotion of promotions) {
      const code = this.getPromotionCurrencyCode(promotion);
      if (!code || seen.has(code)) {
        continue;
      }
      seen.add(code);
      codes.push(code);
    }

    return codes;
  }

  private getPromotionCurrencyCode(promotion: any): string {
    return (
      promotion?.exchangeRate?.currency?.name ||
      promotion?.exchangeRate?.currency?.code ||
      ''
    ).trim();
  }
}
